import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import {compose} from "mu-lambda";
import {useContext, useEffect, useMemo, useState} from "react";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    enums,
    usePublishAnalyticsEvent,
    withConversionContext,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {
    getSelectedDateRange,
    type DateRangeSelection,
    type DateRangeValue,
} from "@khan/component-date-range-dropdown";
import {useQuery} from "@khan/data-gql";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";

import {useUpdatePageSettings} from "~/data-access/use-update-page-settings.ts";

import type {ColumnName} from "../column-names.ts";

import type {guideTranscripts_aiGuideThreads_threads as AIGuideThreads} from "./__graphql-types__/guideTranscripts.ts";
import {GuideTranscriptsContent} from "./guide-transcripts-content.tsx";
import {GuideTranscriptsFilters} from "./guide-transcripts-filters.tsx";
import {GuideTranscriptsHeading} from "./guide-transcripts-heading.tsx";
import {GuideTranscriptsPagination} from "./guide-transcripts-pagination.tsx";
import {GUIDE_TRANSCRIPTS_QUERY} from "./guide-transcripts-query.ts";

// Constants
const DEFAULT_DATE_RANGE_VALUE: DateRangeValue = "last-30-days";
const GUIDE_TRANSCRIPTS_QUERY_PAGE_SIZE = 10;
const CLASSROOM_ENTRYPOINT = "classroom";
const TUTOR_ME_PATH = "/tutor-me";

// Types
type Relationship = "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";

interface OwnProps {
    /**
     * The kaid of the user whose chat history is being viewed.
     */
    kaid: string;
    /**
     * URL path to the user's profile, needed for conversation links.
     */
    profilePath?: string;
    /**
     * The relationship between the viewer and the profile owner. Used for
     * analytics purposes.
     */
    relationship: Relationship;
    /**
     * Show the new table view instead of the conversations view.
     */
    renderTableView?: boolean;
    /**
     * For the conversations view, if a conversation is flagged, render the
     * notice in the first column, under the Chat Name.
     */
    showFlaggedNotice?: boolean;
    /**
     * The columns to display in the table. Defaults to all columns.
     * The values are the keys of the ThreadType object. Order matters.
     */
    visibleColumns?: Array<ColumnName>;
    /**
     * The title to display in the table view.
     */
    tableTitle?: string;
    /**
     * Node which shows up aside from the title in the table view
     */
    tableBannerNode?: React.ReactNode;
    /**
     * Whether to reduce spacing between heading and content (useful when banner is present without title)
     */
    reduceSpacing?: boolean;
    /**
     * Pass true to not show the resume thread link in the chat modal.
     */
    hideResumeThreadLink?: boolean;
    /**
     * The name of the config to use for the query.
     */
    configName?: string;
}

type Props = OwnProps & WithMarkConversionProps;

/**
 * Gets the correct ISO datetime strings for the given date range.
 *
 * For start dates, we modify the time to the start of the day, while for end
 * dates, we modify the time to the very end of the day before converting to
 * ISO format. Returns null values if there is not a valid date or date range
 * provided.
 */
const dateRangeToISOStrings = (
    selection: DateRangeSelection,
): {startDate: string | null; endDate: string | null} => {
    const dateRange = selection.dateRange;
    const startDate = dateRange?.startDate
        ? moment(dateRange.startDate).startOf("day").toISOString()
        : null;
    const endDate = dateRange?.endDate
        ? moment(dateRange.endDate).endOf("day").toISOString()
        : null;
    return {startDate, endDate};
};

/**
 * Custom hook to manage date range state and sync with URL parameters.
 */
const useDateRangeState = (defaultDateRangeValue: DateRangeValue | null) => {
    const {
        queryParams: {
            startDate: queryParamsStartDate,
            endDate: queryParamsEndDate,
        },
    } = useLocation();

    const [dateRangeSelection, setDateRangeSelection] =
        useState<DateRangeSelection>(
            getSelectedDateRange(null, defaultDateRangeValue),
        );

    useEffect(() => {
        if (queryParamsStartDate && queryParamsEndDate) {
            setDateRangeSelection({
                selectedValue: "custom",
                dateRange: {
                    startDate: moment(queryParamsStartDate).toDate(),
                    endDate: moment(queryParamsEndDate).toDate(),
                },
            });
        }
    }, [queryParamsStartDate, queryParamsEndDate]);

    return {dateRangeSelection, setDateRangeSelection};
};

/**
 * Custom hook to manage flagged threads filter state.
 */
const useFlaggedThreadsFilter = () => {
    const {
        queryParams: {selectFlaggedChats},
    } = useLocation();

    const [flaggedThreadsOnly, setFlaggedThreadsOnly] =
        useState<boolean>(!!selectFlaggedChats);

    return {flaggedThreadsOnly, setFlaggedThreadsOnly};
};

/**
 * Custom hook to determine filter visibility logic.
 */
const useFilterVisibility = (
    relationship: Relationship,
    onClassroomExperience: boolean,
    pathname: string,
    dateRangeSelection: DateRangeSelection,
    flaggedThreadsOnly: boolean,
) => {
    const mayHaveTutorMeChats = useMemo(
        () => dateRangeSelection?.selectedValue === "*" || flaggedThreadsOnly,
        [dateRangeSelection?.selectedValue, flaggedThreadsOnly],
    );

    const isTutorMePage = useMemo(
        () => pathname.includes(TUTOR_ME_PATH),
        [pathname],
    );

    const shouldShowFilters = useMemo(
        () =>
            relationship === "COACH" ||
            relationship === "DISTRICT_ADMIN" ||
            onClassroomExperience,
        [relationship, onClassroomExperience],
    );

    return {
        mayHaveTutorMeChats,
        isTutorMePage,
        shouldShowFilters,
    };
};

/**
 * Custom hook to handle analytics events.
 */
const useAnalyticsEvents = (relationship: Relationship) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    useEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideHistoryOpened",
            eventSchemaVersion: 3,
            reviewerRelationship: relationship,
        });

        return function cleanup() {
            publishAnalyticsEvent({
                eventType: "AiGuideHistoryClosed",
                eventSchemaVersion: 3,
                reviewerRelationship: relationship,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want this to run once on mount.
    }, []);
};

/**
 * Renders a view of AI guide transcripts (chat history) with various filtering
 * options. NOTE: this is used in both Classic Khan and Classroom.
 */
const GuideTranscripts = ({
    kaid,
    markConversion,
    profilePath,
    relationship,
    renderTableView,
    showFlaggedNotice,
    visibleColumns,
    tableTitle,
    tableBannerNode,
    reduceSpacing,
    hideResumeThreadLink,
    configName,
}: Props): React.ReactElement => {
    const {entrypoint} = useContext(AppShellContext);
    const onClassroomExperience = entrypoint === CLASSROOM_ENTRYPOINT;

    // Determine default date range based on experience
    const defaultDateRangeValue = onClassroomExperience
        ? null // null === "All time"/ no date range selected
        : DEFAULT_DATE_RANGE_VALUE;

    // Custom hooks
    const {dateRangeSelection, setDateRangeSelection} = useDateRangeState(
        defaultDateRangeValue,
    );
    const {flaggedThreadsOnly, setFlaggedThreadsOnly} =
        useFlaggedThreadsFilter();

    const {pathname} = useLocation();
    const {mayHaveTutorMeChats, isTutorMePage, shouldShowFilters} =
        useFilterVisibility(
            relationship,
            onClassroomExperience,
            pathname,
            dateRangeSelection,
            flaggedThreadsOnly,
        );

    // Analytics and page setup
    useOnMountEffect(() => {
        markConversion({id: "guide_transcripts_page_view"});
    });
    useUpdatePageSettings({title: t`Khanmigo Chat History`});
    useAnalyticsEvents(relationship);

    // Query data
    const {startDate, endDate} = dateRangeToISOStrings(dateRangeSelection);
    const {cursor} = useLocation().queryParams;

    const [result] = useQuery(GUIDE_TRANSCRIPTS_QUERY, {
        variables: {
            kaid,
            cursor,
            startDate,
            endDate,
            moderationStatus: flaggedThreadsOnly ? "FLAGGED" : "ALL",
            limit: GUIDE_TRANSCRIPTS_QUERY_PAGE_SIZE,
            configName,
        },
    });

    // Extract data from query result
    let threads: Array<AIGuideThreads> = [];
    let cursorResult: string | null | undefined = null;

    if (result.status === "success") {
        threads = result.data?.aiGuideThreads?.threads || [];
        cursorResult = result.data?.aiGuideThreads?.cursor || null;
    }

    // Determine if we should render the history section at all
    const isDefaultFilters =
        dateRangeSelection?.selectedValue === "*" && !flaggedThreadsOnly;
    const shouldHideHistorySection =
        isTutorMePage && threads.length === 0 && isDefaultFilters;

    if (shouldHideHistorySection) {
        return <></>;
    }

    /**
     * Determines whether to render the loaded content based on page type.
     */
    const shouldRenderContent = (): boolean => {
        if (isTutorMePage) {
            // For tutor-me pages, show content if we have threads or if we're using default filters
            return threads.length > 0 || mayHaveTutorMeChats;
        }
        return true;
    };

    return (
        <View>
            <GuideTranscriptsHeading
                renderTableView={renderTableView}
                tableTitle={tableTitle}
                tableBannerNode={tableBannerNode}
                shouldShowFilters={shouldShowFilters}
                profilePath={profilePath}
                dateRangeSelection={dateRangeSelection}
                setDateRangeSelection={setDateRangeSelection}
                flaggedThreadsOnly={flaggedThreadsOnly}
                setFlaggedThreadsOnly={setFlaggedThreadsOnly}
            />
            {renderTableView && shouldShowFilters && (
                <GuideTranscriptsFilters
                    dateRangeSelection={dateRangeSelection}
                    setDateRangeSelection={setDateRangeSelection}
                    flaggedThreadsOnly={flaggedThreadsOnly}
                    setFlaggedThreadsOnly={setFlaggedThreadsOnly}
                    userIsLearner={relationship === "SELF"}
                />
            )}
            <View
                style={
                    reduceSpacing ? styles.smallSpacing : styles.largeSpacing
                }
            />
            {shouldRenderContent() && (
                <GuideTranscriptsContent
                    result={result}
                    threads={threads}
                    renderTableView={renderTableView}
                    showFlaggedNotice={showFlaggedNotice}
                    visibleColumns={visibleColumns}
                    hideResumeThreadLink={hideResumeThreadLink}
                    relationship={relationship}
                    profilePath={profilePath}
                    hasAppliedFilters={!isDefaultFilters}
                />
            )}
            <GuideTranscriptsPagination
                renderTableView={renderTableView}
                cursor={cursor}
                cursorResult={cursorResult}
                pathname={pathname}
            />
            <View style={styles.mediumSpacing} />
        </View>
    );
};

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof GuideTranscripts,
        React.ComponentProps<typeof GuideTranscripts>
    >
>;

const GuideTranscriptsWithComposed = compose(
    withConversionContext({
        activity: enums.Activity.OTHER_ACTIVITY,
    }),
    withMarkConversion,
)(GuideTranscripts) as React.ComponentType<ExportProps>;
export {GuideTranscriptsWithComposed as GuideTranscripts};

const styles = StyleSheet.create({
    largeSpacing: {
        height: sizing.size_160,
    },
    smallSpacing: {
        height: sizing.size_160,
    },
    mediumSpacing: {
        height: sizing.size_100,
    },
});
